/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import CheckIcon from '@material-ui/icons/Check';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { runElkCompoundLayout } from '../networkPolicy/elkLayout';
import type { ElkPoint, ElkRect } from '../networkPolicy/elkLayout';
import { KgDoc } from './types';
import { KgRepoMeta } from './useKnowledgeGraphApi';
import { formatBytes, formatRelativeTime } from './relativeTime';
import { edgeId,
  RelationFamily,
  SymbolKind,
  buildRepoGraphModel,
  degreeBucket,
  nodeMatches,
  symbolKind,
} from './repoGraphModel';
import { CameraApi, CanvasPalette, RepoGraphCanvas } from './RepoGraphCanvas';
import { NodeSize, makeNodeMeasurer } from './textMeasure';

// Level-1 (symbol altitude) view. ELK owns placement and routes; painting is
// the GPU-path canvas in RepoGraphCanvas (pan/zoom/cull/LOD at any scale —
// overdrive direction C replaced the DOM node layer). Everything semantic
// stays DOM: toolbar, legend chips, detail panel, and the table view, which
// is the screen-reader path. Toggles and search never re-layout — the graph
// must not jump underfoot; only the unconnected filter re-layouts.

const CANVAS_HEIGHT = 'max(480px, calc(100vh - 430px))';
const BUCKET_FONT = [11, 12, 13, 14] as const;
const BUCKET_WEIGHT = [400, 400, 500, 600] as const;
const ZOOM_STEP = 1.25;

type Positioned = {
  positions: Map<string, ElkPoint>;
  groupBounds: Map<string, ElkRect>;
  routes: Map<string, ElkPoint[]>;
  width: number;
  height: number;
};

// Layouts are pure functions of (artifact sha, filter) — cache across
// selection switches and remounts so revisiting a repo is instant.
const layoutCache = new Map<string, Positioned>();

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
    fontSize: 12,
    '@media (pointer: coarse)': {
      '& button': { minHeight: 36, minWidth: 36 },
    },
  },
  provenance: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: theme.spacing(2.5),
    rowGap: theme.spacing(0.5),
    fontSize: 12,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  provLabel: {
    color: theme.palette.text.secondary,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginRight: theme.spacing(0.75),
  },
  provStore: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  provPath: {
    fontFamily: 'monospace',
    fontSize: 11,
    maxWidth: 420,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  provLink: {
    color: 'inherit',
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    textDecorationColor: theme.palette.text.secondary,
    textUnderlineOffset: 2,
    '&:hover': { textDecorationStyle: 'solid' },
  },
  legendRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
    fontSize: 12,
    '@media (pointer: coarse)': {
      '& button, & [role="button"]': { minHeight: 36 },
    },
  },
  kindLegend: {
    display: 'inline-flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
  canvasOuter: {
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    background: theme.palette.background.default,
    '&:focus-within': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  tableWrap: {
    maxHeight: CANVAS_HEIGHT,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },
  rowButton: {
    textTransform: 'none',
    padding: theme.spacing(0, 0.5),
    minWidth: 0,
    fontWeight: 500,
  },
  detailPanel: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 2,
    width: 300,
    maxWidth: '75%',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    boxShadow: theme.shadows[3],
    fontSize: 12,
  },
  detailTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  detailFile: {
    color: theme.palette.text.secondary,
    wordBreak: 'break-all',
    margin: theme.spacing(0.5, 0),
  },
  neighborButton: {
    textTransform: 'none',
    padding: theme.spacing(0, 0.5),
    minWidth: 0,
    fontWeight: 500,
    fontSize: 12,
  },
}));

// Per-family stroke styling shared by the canvas and the legend swatches.
// Color is never the only cue: every family carries a dash pattern or width.
// Tokens verified >=3:1 against background.default in both themes.
const familyStrokes = (
  theme: Theme,
): Record<RelationFamily, { stroke: string; dash: number[]; width: number }> => ({
  contains: { stroke: theme.palette.text.secondary, dash: [2, 4], width: 1 },
  references: { stroke: theme.palette.text.secondary, dash: [], width: 1 },
  imports: { stroke: theme.palette.info.main, dash: [6, 4], width: 1.2 },
  calls: { stroke: theme.palette.success.main, dash: [], width: 1.6 },
  inherits: { stroke: theme.palette.primary.main, dash: [], width: 2 },
  defines: { stroke: theme.palette.warning.main, dash: [2, 3], width: 1.4 },
  other: { stroke: theme.palette.text.secondary, dash: [1, 3], width: 1 },
});

// Kind color lives in the caption text (the word itself is always present,
// so color is never the only cue). All pairs clear 4.5:1 on paper.
const kindColor = (kind: SymbolKind, theme: Theme): string => {
  switch (kind) {
    case 'class':
      return theme.palette.primary.main;
    case 'function':
      return theme.palette.success.main;
    case 'concept':
      return theme.palette.warning.main;
    default:
      return theme.palette.text.secondary;
  }
};

// The GitHub commit behind a build, recovered from the store path so the
// decorated repoName ("slug · path") in giant views never breaks the link.
const githubCommitUrl = (meta: KgRepoMeta): string | null => {
  const m = meta.location.match(/\/repo\/([\w.-]+)\/([\w.-]+)\/([0-9a-f]{7,40})\/$/);
  return m ? `https://github.com/${m[1]}/${m[2]}/commit/${m[3]}` : null;
};

export const RepoGraph = (props: {
  doc: KgDoc;
  repoName: string;
  // Present on synthetic directory-overview docs: renders a drill action in
  // the detail panel (PLAT-1534 giant graphs).
  onOpenNode?: (id: string) => void;
  openNodeLabel?: string;
  // Synthetic directory-overview docs: copy says directories, kind pills
  // and the kinds legend (symbol taxonomy) stay out of the way.
  dirMode?: boolean;
  // Stored-object provenance for the toolbar: size/encoding in the store,
  // where the sha-addressed set lives, and when it was published.
  meta?: KgRepoMeta | null;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const cameraRef = useRef<CameraApi | null>(null);
  const [layout, setLayout] = useState<Positioned | null>(null);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [layoutProgress, setLayoutProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [layoutElapsedS, setLayoutElapsedS] = useState(0);
  const [mainThreadFallback, setMainThreadFallback] = useState(false);
  const layoutAbortRef = useRef<AbortController | null>(null);
  const [layoutTick, setLayoutTick] = useState(0);
  const [hiddenFamilies, setHiddenFamilies] = useState<Set<RelationFamily>>(
    new Set(),
  );
  const [query, setQuery] = useState('');
  const [matchIdx, setMatchIdx] = useState(0);
  const [view, setView] = useState<'graph' | 'table'>('graph');
  const [zoomPct, setZoomPct] = useState(100);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const focusFade = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hideUnconnected, setHideUnconnected] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const narrow = useMediaQuery(theme.breakpoints.down('xs'));
  const [legendOpen, setLegendOpen] = useState(false);

  const model = useMemo(() => buildRepoGraphModel(props.doc), [props.doc]);
  const connected = useMemo(
    () => ({
      groups: model.groups.filter(
        g =>
          (model.degree.get(g.container.id) ?? 0) > 0 ||
          g.members.some(m => (model.degree.get(m.id) ?? 0) > 0),
      ),
      loose: model.loose.filter(n => (model.degree.get(n.id) ?? 0) > 0),
    }),
    [model],
  );
  const viewGroups = hideUnconnected ? connected.groups : model.groups;
  const viewLoose = hideUnconnected ? connected.loose : model.loose;
  const leafNodes = useMemo(
    () => [...viewGroups.flatMap(g => g.members), ...viewLoose],
    [viewGroups, viewLoose],
  );
  const totalLeafCount = useMemo(
    () => model.groups.reduce((a, g) => a + g.members.length, 0) + model.loose.length,
    [model],
  );
  const unconnectedCount =
    totalLeafCount -
    (connected.groups.reduce((a, g) => a + g.members.length, 0) +
      connected.loose.length);
  const labelOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of props.doc.nodes) m.set(n.id, n.label ?? n.id);
    return m;
  }, [props.doc]);

  const bucketOf = useCallback(
    (id: string) => degreeBucket(model.degree.get(id) ?? 0),
    [model],
  );
  const bucketFont = useCallback(
    (id: string) => {
      const b = bucketOf(id);
      return { px: BUCKET_FONT[b], weight: BUCKET_WEIGHT[b] };
    },
    [bucketOf],
  );

  // Canvas-measured node sizes (replaces the retired hidden-DOM measure pass).
  const nodeSizes = useMemo(() => {
    const fontFamily = theme.typography.fontFamily ?? 'Helvetica, Arial, sans-serif';
    const measure = makeNodeMeasurer(fontFamily);
    const sizes = new Map<string, NodeSize>();
    for (const n of leafNodes) {
      const { px, weight } = bucketFont(n.id);
      sizes.set(n.id, measure(n, px, weight));
    }
    return sizes;
  }, [leafNodes, bucketFont, theme.typography.fontFamily]);

  // ELK layout, cached by (artifact sha, filter) so re-visits are instant.
  useEffect(() => {
    let cancelled = false;
    const key = `${props.repoName}@${props.doc.built_at_commit ?? 'nosha'}:${hideUnconnected}`;
    const cached = layoutCache.get(key);
    if (cached) {
      setLayout(cached);
      setLayoutError(null);
      return undefined;
    }
    setLayout(null);
    setLayoutProgress(null);
    setMainThreadFallback(false);
    // Aborting on cleanup terminates the layout worker, so switching repo
    // (or unmounting) mid-layout stops the compute instead of orphaning it.
    const ac = new AbortController();
    layoutAbortRef.current = ac;
    (async () => {
      try {
        const size = (id: string) => {
          const s = nodeSizes.get(id)!;
          return { id, width: s.width, height: s.height };
        };
        const res = await runElkCompoundLayout({
          groups: viewGroups.map(g => ({
            id: g.container.id,
            children: g.members.map(m => size(m.id)),
          })),
          nodes: viewLoose.map(n => size(n.id)),
          edges: model.drawnEdges.map((e, i) => ({
            id: edgeId(e, i),
            source: String(e.source),
            target: String(e.target),
          })),
          padding: { top: 30, side: 12, bottom: 12 },
        }, {
          signal: ac.signal,
          onProgress: (done, total) => {
            if (!cancelled) setLayoutProgress({ done, total });
          },
          onFallback: () => {
            if (!cancelled) setMainThreadFallback(true);
          },
        });
        if (cancelled) return;
        const done = { ...res, width: res.width + 40, height: res.height + 40 };
        layoutCache.set(key, done);
        setLayout(done);
        setLayoutError(null);
      } catch (e) {
        // Chunk-load failure: elkLayout cleared its import memo; Retry re-runs.
        // A user-initiated Cancel also lands here (as an AbortError) and is
        // not a failure — name it as the choice it was.
        if (!cancelled) {
          setLayoutError(
            (e as Error)?.name === 'AbortError'
              ? 'layout cancelled'
              : String((e as Error).message ?? e),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
      // Cleanup aborts are silent (!cancelled-gated); a user Cancel aborts
      // while cancelled=false and surfaces as 'layout cancelled' above.
      ac.abort();
    };
  }, [model, viewGroups, viewLoose, nodeSizes, hideUnconnected, props.repoName, props.doc, layoutTick]);

  // Elapsed ticker while a layout runs — a status line that counts is the
  // difference between "working" and "stuck".
  useEffect(() => {
    if (layout || layoutError) {
      setLayoutElapsedS(0);
      return undefined;
    }
    const started = Date.now();
    const t = setInterval(
      () => setLayoutElapsedS(Math.floor((Date.now() - started) / 1000)),
      1000,
    );
    return () => clearInterval(t);
  }, [layout, layoutError, layoutTick]);

  const filterActive = query.trim().length > 0;
  const matchList = useMemo(
    () => (filterActive ? leafNodes.filter(n => nodeMatches(n, query)) : []),
    [filterActive, leafNodes, query],
  );
  const matches = useMemo(
    () => (filterActive ? new Set(matchList.map(n => n.id)) : null),
    [filterActive, matchList],
  );
  const groupMatches = useMemo(() => {
    if (!matches) return null;
    const set = new Set<string>();
    for (const g of model.groups) {
      if (nodeMatches(g.container, query) || g.members.some(m => matches.has(m.id))) {
        set.add(g.container.id);
      }
    }
    return set;
  }, [matches, model.groups, query]);

  // Search navigation: first match centers when the query settles (debounced);
  // Enter / Shift+Enter cycle. Centering raises zoom to legibility.
  const activeMatchId = matchList[matchIdx]?.id ?? null;
  useEffect(() => {
    setMatchIdx(0);
    if (!matchList.length || view !== 'graph') return undefined;
    const t = setTimeout(() => cameraRef.current?.centerOn(matchList[0].id, 1), 200);
    return () => clearTimeout(t);
    // Only when the query itself changes — not on camera churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const stepMatch = (dir: 1 | -1) => {
    if (!matchList.length) return;
    const next = (matchIdx + dir + matchList.length) % matchList.length;
    setMatchIdx(next);
    cameraRef.current?.centerOn(matchList[next].id, 1);
  };

  // Table-row activation lands the graph on that symbol, ringed and zoomed in.
  useEffect(() => {
    if (view === 'graph' && pendingFocusId && layout) {
      const id = pendingFocusId;
      requestAnimationFrame(() => cameraRef.current?.centerOn(id, 1));
      setFocusedId(id);
      if (focusFade.current) clearTimeout(focusFade.current);
      focusFade.current = setTimeout(() => setFocusedId(null), 2500);
      setPendingFocusId(null);
    }
  }, [view, pendingFocusId, layout]);

  useEffect(
    () => () => {
      if (focusFade.current) clearTimeout(focusFade.current);
    },
    [],
  );

  const toggleFamily = (fam: RelationFamily) =>
    setHiddenFamilies(prev => {
      const next = new Set(prev);
      if (next.has(fam)) next.delete(fam);
      else next.add(fam);
      return next;
    });

  const strokes = useMemo(() => familyStrokes(theme), [theme]);
  const canvasPalette = useMemo(
    (): CanvasPalette => ({
      canvasBg: theme.palette.background.default,
      nodeBg: theme.palette.background.paper,
      nodeBorder: theme.palette.divider,
      groupBg: theme.palette.action.hover,
      groupBorder: theme.palette.divider,
      headerText: theme.palette.text.secondary,
      labelText: theme.palette.text.primary,
      ring: theme.palette.primary.main,
      kind: {
        class: kindColor('class', theme),
        function: kindColor('function', theme),
        concept: kindColor('concept', theme),
        symbol: kindColor('symbol', theme),
      },
      family: strokes,
    }),
    [theme, strokes],
  );

  const families = [...model.familyCounts.entries()].sort((a, b) => b[1] - a[1]);
  const tableRows = filterActive ? matchList : leafNodes;

  const selectedNode = selectedId
    ? leafNodes.find(n => n.id === selectedId) ?? null
    : null;
  const selectedEdges = useMemo(() => {
    if (!selectedId) return [];
    return model.drawnEdges
      .filter(e => String(e.source) === selectedId || String(e.target) === selectedId)
      .map(e => {
        const outbound = String(e.source) === selectedId;
        return {
          relation: e.relation ?? 'edge',
          count: e.count,
          outbound,
          otherId: outbound ? String(e.target) : String(e.source),
        };
      });
  }, [model, selectedId]);

  const jumpTo = (id: string) => {
    // Containers (files) aren't selectable leaves: selecting one would just
    // blank the panel — keep the current selection and center on the group.
    if (leafNodes.some(n => n.id === id)) setSelectedId(id);
    cameraRef.current?.centerOn(id, 1);
  };

  const onZoomChange = useCallback((z: number) => {
    setZoomPct(prev => {
      const next = Math.round(z * 100);
      return next === prev ? prev : next;
    });
  }, []);

  return (
    <div>
      <div className={classes.toolbar} role="group" aria-label="graph tools">
        <TextField
          id="kg-symbol-search"
          size="small"
          variant="outlined"
          label={props.dirMode ? 'Find directory' : 'Find symbol'}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              stepMatch(e.shiftKey ? -1 : 1);
            }
          }}
          InputProps={{
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="clear search"
                  onClick={() => setQuery('')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        {/* Always rendered so typing a query never shifts the view buttons. */}
        <Tooltip title="Previous match (Shift+Enter)">
          <span>
            <IconButton
              size="small"
              aria-label="previous match"
              disabled={matchList.length < 2}
              onClick={() => stepMatch(-1)}
            >
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Next match (Enter)">
          <span>
            <IconButton
              size="small"
              aria-label="next match"
              disabled={matchList.length < 2}
              onClick={() => stepMatch(1)}
            >
              <NavigateNextIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          size="small"
          variant={view === 'graph' ? 'contained' : 'outlined'}
          disableElevation
          aria-pressed={view === 'graph'}
          onClick={() => setView('graph')}
        >
          Graph
        </Button>
        <Button
          size="small"
          variant={view === 'table' ? 'contained' : 'outlined'}
          disableElevation
          aria-pressed={view === 'table'}
          onClick={() => setView('table')}
        >
          Table
        </Button>
        {view === 'graph' && (
          <>
            <Tooltip title="Zoom out (-)">
              <IconButton
                size="small"
                aria-label="zoom out"
                onClick={() => cameraRef.current?.zoomBy(1 / ZOOM_STEP)}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset to 100%">
              <Button
                size="small"
                aria-label={`zoom ${zoomPct} percent — reset to 100`}
                onClick={() => cameraRef.current?.zoomTo(1)}
                style={{ minWidth: 48, textTransform: 'none' }}
              >
                {zoomPct}%
              </Button>
            </Tooltip>
            <Tooltip title="Zoom in (+)">
              <IconButton
                size="small"
                aria-label="zoom in"
                onClick={() => cameraRef.current?.zoomBy(ZOOM_STEP)}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button size="small" variant="outlined" onClick={() => cameraRef.current?.fit()}>
              Fit
            </Button>
          </>
        )}
        <span>
          {props.dirMode
            ? `${leafNodes.length} directories · ${model.drawnEdges.length} aggregated connections`
            : `${leafNodes.length} symbols in ${viewGroups.length} files · ${model.drawnEdges.length} edges`}
        </span>
        {matches && (
          <span aria-live="polite">
            {matchList.length
              ? `match ${matchIdx + 1} of ${matchList.length}: ${
                  matchList[matchIdx]?.label ?? matchList[matchIdx]?.id ?? ''
                }`
              : 'no matching nodes'}
          </span>
        )}
      </div>
      {narrow && (
        <Button
          size="small"
          aria-expanded={legendOpen}
          onClick={() => setLegendOpen(v => !v)}
          endIcon={legendOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          style={{ textTransform: 'none', marginBottom: 4 }}
        >
          Legend & filters
        </Button>
      )}
      {props.meta && (
        <div className={classes.provenance} role="group" aria-label="graph provenance">
          <span>
            <span className={classes.provLabel}>Built from</span>
            {githubCommitUrl(props.meta) ? (
              <a
                className={classes.provLink}
                href={githubCommitUrl(props.meta) as string}
                target="_blank"
                rel="noopener noreferrer"
              >
                @{props.meta.sha.slice(0, 7)}
              </a>
            ) : (
              <>@{props.meta.sha.slice(0, 7)}</>
            )}
          </span>
          {props.meta.updatedAt && formatRelativeTime(props.meta.updatedAt) && (
            <span title={new Date(props.meta.updatedAt).toLocaleString()}>
              <span className={classes.provLabel}>Published</span>
              {formatRelativeTime(props.meta.updatedAt)}
            </span>
          )}
          {typeof props.meta.bytes === 'number' && (
            <span>
              <span className={classes.provLabel}>Graph</span>
              {formatBytes(props.meta.bytes)}
              {props.meta.encoding === 'gzip' ? ' gzipped' : ''}
            </span>
          )}
          {typeof props.meta.objects === 'number' &&
            typeof props.meta.setBytes === 'number' && (
            <span>
              <span className={classes.provLabel}>Artifact set</span>
              {props.meta.objects} object{props.meta.objects === 1 ? '' : 's'} ·{' '}
              {formatBytes(props.meta.setBytes)}
            </span>
          )}
          <span className={classes.provStore}>
            <span className={classes.provLabel}>Stored at</span>
            <span className={classes.provPath} title={props.meta.location}>
              {props.meta.location}
            </span>
            {!!navigator.clipboard && (
              <Tooltip title={copied ? 'Copied' : 'Copy store location'}>
                <IconButton
                  size="small"
                  aria-label={copied ? 'copied' : 'copy store location'}
                  onClick={() => {
                    // Flip only on a confirmed write — a rejected or absent
                    // clipboard must not claim success.
                    navigator.clipboard
                      .writeText(props.meta?.location ?? '')
                      .then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                      })
                      .catch(() => {});
                  }}
                >
                  {copied ? (
                    <CheckIcon fontSize="inherit" />
                  ) : (
                    <FileCopyOutlinedIcon fontSize="inherit" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </span>
        </div>
      )}
      {(!narrow || legendOpen) && (
        <div className={classes.legendRow} role="group" aria-label="edge and kind legend">
          {families.map(([fam, count]) => {
            const s = strokes[fam];
            const hidden = hiddenFamilies.has(fam);
            return (
              <Chip
                key={fam}
                size="small"
                variant={hidden ? 'outlined' : 'default'}
                onClick={() => toggleFamily(fam)}
                aria-pressed={!hidden}
                style={hidden ? { opacity: 0.5 } : undefined}
                label={
                  <span>
                    <svg width={26} height={8} aria-hidden style={{ marginRight: 4 }}>
                      <line
                        x1={1}
                        y1={4}
                        x2={25}
                        y2={4}
                        stroke={s.stroke}
                        strokeWidth={s.width}
                        strokeDasharray={s.dash.join(' ') || undefined}
                      />
                    </svg>
                    {fam} · {count}
                  </span>
                }
              />
            );
          })}
          {unconnectedCount > 0 && (
            <Chip
              size="small"
              variant={hideUnconnected ? 'outlined' : 'default'}
              onClick={() => setHideUnconnected(v => !v)}
              aria-pressed={!hideUnconnected}
              style={hideUnconnected ? { opacity: 0.5 } : undefined}
              label={`unconnected · ${unconnectedCount}`}
            />
          )}
          {hiddenFamilies.size === families.length && families.length > 0 && (
            <span role="status">all edge families hidden</span>
          )}
          {!props.dirMode && (
            <span className={classes.kindLegend}>
              <span style={{ color: theme.palette.text.secondary }}>kinds:</span>
              {(['class', 'function', 'concept', 'symbol'] as SymbolKind[]).map(k => (
                <span
                  key={k}
                  style={{ color: kindColor(k, theme), fontSize: 11, fontWeight: 600 }}
                >
                  {k}
                </span>
              ))}
            </span>
          )}
        </div>
      )}
      {layoutError && (
        <div role="alert">
          {layoutError.startsWith('canvas draw failed:') || layoutError === 'layout cancelled'
            ? layoutError
            : `layout failed: ${layoutError}`}{' '}
          <Button size="small" variant="outlined" onClick={() => setLayoutTick(x => x + 1)}>
            Retry
          </Button>
        </div>
      )}
      {view === 'table' ? (
        <TableContainer className={classes.tableWrap}>
          <Table size="small" stickyHeader aria-label={`${props.dirMode ? 'directories' : 'symbols'} in ${props.repoName}`}>
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>File</TableCell>
                <TableCell align="right">Connections</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>No symbols match “{query.trim()}”.</TableCell>
                </TableRow>
              )}
              {tableRows.map(n => {
                const kind = symbolKind(n);
                return (
                  <TableRow key={n.id} hover>
                    <TableCell>
                      <Button
                        size="small"
                        className={classes.rowButton}
                        onClick={() => {
                          setSelectedId(n.id);
                          setPendingFocusId(n.id);
                          setView('graph');
                        }}
                        title="Show in graph"
                      >
                        {n.label ?? n.id}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <span style={{ color: kindColor(kind, theme), fontWeight: 600 }}>
                        {kind}
                      </span>
                    </TableCell>
                    <TableCell>
                      {n.source_file
                        ? `${n.source_file}${n.source_location ? `:${n.source_location}` : ''}`
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{model.degree.get(n.id) ?? 0}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <div className={classes.canvasOuter} style={{ height: CANVAS_HEIGHT }}>
          {selectedNode && (
            <div
              className={classes.detailPanel}
              role="region"
              aria-label={`details for ${selectedNode.label ?? selectedNode.id}`}
            >
              <div className={classes.detailTitleRow}>
                <strong>{selectedNode.label ?? selectedNode.id}</strong>
                <IconButton
                  size="small"
                  aria-label="close details"
                  onClick={() => setSelectedId(null)}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </div>
              <div>
                <span
                  style={{
                    color: props.dirMode
                      ? theme.palette.text.secondary
                      : kindColor(symbolKind(selectedNode), theme),
                    fontWeight: 600,
                  }}
                >
                  {props.dirMode ? 'directory' : symbolKind(selectedNode)}
                </span>{' '}
                · {selectedEdges.length} connection{selectedEdges.length === 1 ? '' : 's'}
              </div>
              {props.onOpenNode && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => props.onOpenNode!(selectedNode.id)}
                >
                  {props.openNodeLabel ?? 'Open'}
                </Button>
              )}
              {selectedNode.source_file && (
                <div className={classes.detailFile}>
                  {selectedNode.source_file}
                  {selectedNode.source_location ? `:${selectedNode.source_location}` : ''}
                </div>
              )}
              {selectedEdges.slice(0, 8).map((e, i) => (
                <div key={`${e.otherId}-${i}`}>
                  {e.outbound
                    ? `${e.relation}${e.count ? ` ·${e.count.toLocaleString()}` : ''} → `
                    : `← ${e.relation}${e.count ? ` ·${e.count.toLocaleString()}` : ''} `}
                  <Button
                    size="small"
                    className={classes.neighborButton}
                    onClick={() => jumpTo(e.otherId)}
                    title="Select and center this symbol"
                  >
                    {labelOf.get(e.otherId) ?? e.otherId}
                  </Button>
                </div>
              ))}
              {selectedEdges.length > 8 && (
                <div>+ {selectedEdges.length - 8} more — see the table view</div>
              )}
            </div>
          )}
          {layout ? (
            <RepoGraphCanvas
              layout={layout}
              leafNodes={leafNodes}
              viewGroups={viewGroups}
              drawnEdges={model.drawnEdges}
              nodeSizes={nodeSizes}
              degree={model.degree}
              bucketFont={bucketFont}
              hiddenFamilies={hiddenFamilies}
              matches={matches}
              groupMatches={groupMatches}
              activeIds={[activeMatchId, focusedId, selectedId]}
              fontFamily={theme.typography.fontFamily ?? 'Helvetica, Arial, sans-serif'}
              palette={canvasPalette}
              prefersReducedMotion={prefersReducedMotion}
              height="100%"
              regionLabel={`${props.repoName} ${props.dirMode ? 'directory' : 'symbol'} graph — drag to pan, wheel or +/- to zoom, 0 fits; use the table view for a screen-reader-friendly list`}
              onSelect={setSelectedId}
              onZoomChange={onZoomChange}
              onDrawError={msg => setLayoutError(`canvas draw failed: ${msg}`)}
              cameraRef={cameraRef}
            />
          ) : (
            <div style={{ padding: 16, fontSize: 13 }} role="status">
              Laying out {leafNodes.length} symbols…
              {layoutProgress && layoutProgress.total > 1
                ? ` (cluster ${layoutProgress.done}/${layoutProgress.total})`
                : ''}
              {layoutElapsedS >= 5 ? ` · ${layoutElapsedS}s` : ''}
              {mainThreadFallback
                ? ' — dense graph, laying out on the main thread; the tab may pause'
                : layoutElapsedS >= 20
                  ? ' — dense graphs can take a minute or two'
                  : ''}
              {layoutElapsedS >= 5 && (
                <Button
                  size="small"
                  variant="outlined"
                  style={{ marginLeft: 12 }}
                  onClick={() => layoutAbortRef.current?.abort()}
                >
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
